import {
	STOREFRONT_HEAD_TAG_KEYS,
	STOREFRONT_JSONLD_ATTRIBUTE,
	type StorefrontHeadTagModel,
} from '@supply-mover-2-0/shared';

/**
 * PD-17174 — write the storefront's head tags into the live document.
 *
 * The edge function already wrote the same tags into the HTML the page loaded
 * with; this finds each one by what it is (`meta[name="description"]`,
 * `link[rel="canonical"]`, …) and updates it, so a tag is never doubled, and
 * removes the managed tags the current page does not use (a canonical on a
 * 404, a product's structured data after leaving the product page).
 */
export function applyStorefrontHeadTags(
	doc: Document,
	tags: ReadonlyArray<StorefrontHeadTagModel>,
): void {
	const wanted = new Set(tags.map((tag) => tag.key));

	for (const tag of tags) {
		switch (tag.kind) {
			case 'title':
				if (doc.title !== tag.text) {
					doc.title = tag.text;
				}
				break;
			case 'meta': {
				const element =
					findManagedElement(doc, tag.key) ??
					appendToHead(doc, 'meta', { [tag.attribute]: tag.name });
				if (element.getAttribute('content') !== tag.content) {
					element.setAttribute('content', tag.content);
				}
				break;
			}
			case 'canonical': {
				const element =
					findManagedElement(doc, tag.key) ??
					appendToHead(doc, 'link', { rel: 'canonical' });
				if (element.getAttribute('href') !== tag.href) {
					element.setAttribute('href', tag.href);
				}
				break;
			}
			case 'jsonld': {
				const element =
					findManagedElement(doc, tag.key) ??
					appendToHead(doc, 'script', {
						type: 'application/ld+json',
						[STOREFRONT_JSONLD_ATTRIBUTE]: '',
					});
				if (element.textContent !== tag.json) {
					element.textContent = tag.json;
				}
				break;
			}
		}
	}

	for (const key of STOREFRONT_HEAD_TAG_KEYS) {
		if (key !== 'title' && !wanted.has(key)) {
			findManagedElement(doc, key)?.remove();
		}
	}
}

/** The element a managed tag key names, e.g. `meta:property:og:title`. */
function findManagedElement(doc: Document, key: string): Element | null {
	if (key === 'canonical') {
		return doc.head.querySelector('link[rel="canonical"]');
	}
	if (key === 'jsonld') {
		return doc.head.querySelector(
			`script[type="application/ld+json"][${STOREFRONT_JSONLD_ATTRIBUTE}]`,
		);
	}
	const [, attribute, ...nameParts] = key.split(':');
	const name = nameParts.join(':');
	if (attribute == null || name === '') {
		return null;
	}
	// The names are the fixed keys above (og:title, twitter:card, …), never
	// visitor input, so they go into the selector as they are.
	return doc.head.querySelector(`meta[${attribute}="${name}"]`);
}

function appendToHead(
	doc: Document,
	tagName: 'meta' | 'link' | 'script',
	attributes: Record<string, string>,
): Element {
	const element = doc.createElement(tagName);
	for (const [name, value] of Object.entries(attributes)) {
		element.setAttribute(name, value);
	}
	doc.head.appendChild(element);
	return element;
}
